import { Component, Inject, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SEG } from 'src/app/models/seg.model';
import { SegService } from 'src/app/services/seg.service';
import { ToastService } from 'src/app/services/toast.service';
import { trackById } from 'src/app/shared/utils/track-by.util';
import { SegDeleteMergeResult } from './seg-delete-merge-dialog.model';

export interface ISegDeleteMergeDialogData {
  segId: string;
  segName: string;
  organizationLevelId: string;
}

type SegDeleteMergeStep = 'choice' | 'picker' | 'confirm';
type SegDeleteMergeChoice = 'delete' | 'merge';

@Component({
  standalone: false,
  selector: 'app-seg-delete-merge-dialog',
  templateUrl: './seg-delete-merge-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './seg-delete-merge-dialog.component.scss',
})
export class SegDeleteMergeDialogComponent implements OnDestroy {
  step: SegDeleteMergeStep = 'choice';
  selectedChoice: SegDeleteMergeChoice | null = null;

  searchQuery = '';
  isLoadingCandidates = false;
  candidates: SEG[] = [];
  filteredCandidates: SEG[] = [];
  selectedTarget: SEG | null = null;

  isDeleting = false;
  isSaving = false;
  hasError = false;

  public trackById = trackById;
  private readonly destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<SegDeleteMergeDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ISegDeleteMergeDialogData,
    private segService: SegService,
    private toastService: ToastService,
  ) { }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get canContinueChoice(): boolean {
    return !!this.selectedChoice;
  }

  get canContinuePicker(): boolean {
    return !!this.selectedTarget;
  }

  selectChoice(choice: SegDeleteMergeChoice): void {
    this.selectedChoice = choice;
  }

  continueFromChoice(): void {
    if (!this.selectedChoice) return;

    if (this.selectedChoice === 'delete') {
      this.deleteSeg();
      return;
    }

    this.step = 'picker';
    this.loadCandidates();
  }

  onSearchChange(): void {
    const query = this.searchQuery.trim().toLowerCase();
    this.filteredCandidates = !query
      ? this.candidates
      : this.candidates.filter(candidate =>
        `${candidate.name} ${candidate.code ?? ''}`.toLowerCase().includes(query)
      );
  }

  selectCandidate(candidate: SEG): void {
    this.selectedTarget = candidate;
  }

  continueFromPicker(): void {
    if (!this.selectedTarget) return;
    this.hasError = false;
    this.step = 'confirm';
  }

  backFromPicker(): void {
    this.step = 'choice';
    this.selectedTarget = null;
    this.searchQuery = '';
  }

  backFromConfirm(): void {
    this.hasError = false;
    this.step = 'picker';
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  deleteInsteadFromEmptyPicker(): void {
    this.deleteSeg();
  }

  confirmMerge(): void {
    if (!this.selectedTarget) return;

    this.isSaving = true;
    this.hasError = false;
    this.segService.mergeAndDelete(this.data.segId, this.selectedTarget.id).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        this.isSaving = false;
        this.toastService.showSuccess('SEG merged successfully', true);
        this.dialogRef.close(SegDeleteMergeResult.Merged);
      },
      error: () => {
        this.isSaving = false;
        this.hasError = true;
      },
    });
  }

  private deleteSeg(): void {
    this.isDeleting = true;
    this.segService.delete(this.data.segId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: () => {
        this.isDeleting = false;
        this.toastService.showSuccess('SEG archived successfully', true);
        this.dialogRef.close(SegDeleteMergeResult.Deleted);
      },
      error: (error) => {
        this.isDeleting = false;
        this.toastService.showError(error);
      },
    });
  }

  private loadCandidates(): void {
    this.isLoadingCandidates = true;
    this.segService.getMergeCandidates(this.data.organizationLevelId, this.data.segId).pipe(
      takeUntil(this.destroy$)
    ).subscribe({
      next: (candidates) => {
        this.candidates = candidates;
        this.filteredCandidates = candidates;
        this.isLoadingCandidates = false;
      },
      error: (error) => {
        this.toastService.showError(error);
        this.candidates = [];
        this.filteredCandidates = [];
        this.isLoadingCandidates = false;
      },
    });
  }
}
